import {
  ExclamationCircleOutlineIcon,
  Icon,
  type IconComponent,
} from '@fountain/ripple/icon';
import { Tooltip } from '@fountain/ripple/tooltip';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  APPROVER_PRESENTATION,
  ApproverState,
  approverState,
  ChainApprover,
  LEVEL_PRESENTATION,
  LevelState,
  levelProgress,
  visibleApprovers,
} from 'components/ApprovalLevelChain';
import chainMessages from 'components/ApprovalLevelChain/messages';

import ruleMessages from '../messages';
import { ApprovalRound, approvalRounds, RoundMember } from './approvalRounds';
import messages from './messages';
import { NextStep } from './NextStep';
import { useStyles } from './styles';
import { Approver, Level, RuleGroup } from './types';

interface ApprovalStatusCellProps {
  rules: RuleGroup[];
  /**
   * Whether the request itself has finished.
   *
   * Required by `levelState` rather than derivable from the levels: a cancelled
   * request leaves its open level reading `active`, so a cell that judged each
   * level on its own would say a withdrawn approval was still awaiting somebody.
   */
  requestSettled: boolean;
  /**
   * Whether the reader may manage approval rules, which is what the endpoint
   * behind Replace approver authorizes.
   */
  canManageRules: boolean;
  /** Opens the Replace approver dialog for an approver who left the account. */
  onReplace: (level: Level, approver: Approver) => void;
}

type Mark = 'approved' | 'rejected' | 'pending' | 'blocked' | 'neutral';

const LEVEL_MARK: Record<LevelState, Mark> = {
  approved: 'approved',
  rejected: 'rejected',
  active: 'pending',
  notStarted: 'neutral',
  notTriggered: 'neutral',
  cancelled: 'neutral',
};

const APPROVER_MARK: Record<ApproverState, Mark> = {
  approved: 'approved',
  rejected: 'rejected',
  pending: 'pending',
  cancelled: 'neutral',
};

/**
 * One glyph, tinted for its state. Named when no word beside it says the
 * state, which in this cell is every level line and every approver.
 */
const Glyph: React.FC<{
  icon: IconComponent;
  mark: Mark;
  label?: string;
}> = ({ icon, mark, label }) => {
  const styles = useStyles();
  const markClass = {
    approved: styles.markApproved,
    rejected: styles.markRejected,
    pending: styles.markPending,
    blocked: styles.markRejected,
    neutral: styles.markNeutral,
  }[mark];

  return (
    <Icon
      aria-label={label}
      className={markClass}
      color="inherit"
      icon={icon}
      role={label ? 'img' : undefined}
      size="sm"
    />
  );
};

/**
 * "Replace approver", after the reason an approver who left the account cannot
 * act. It opens the dialog the review panel's Unblock this level opens, which
 * unblocks the level in place, on an offer or an opening.
 *
 * It stops its click, because an offer row opens the review panel on a click
 * anywhere in it.
 */
const ReplaceControl: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const styles = useStyles();

  return (
    <button
      className={styles.replaceApprover}
      type="button"
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
    >
      <FormattedMessage {...messages.replaceApprover} />
    </button>
  );
};

const approverName = (approver: ChainApprover, unnamed: string) =>
  approver.name ?? unnamed;

// The server sets the reason only on an assignment still waiting, and this
// keeps a closed level's row from claiming otherwise.
const blockedReasonOf = (approver: ChainApprover, state: ApproverState) =>
  state === 'pending' ? approver.blockedReason : null;

const ApproverLine: React.FC<{
  approver: ChainApprover;
  state: ApproverState;
  /** Absent for a reader who may not manage approval rules. */
  onReplace?: () => void;
}> = ({ approver, state, onReplace }) => {
  const styles = useStyles();
  const intl = useIntl();
  const name = approverName(
    approver,
    intl.formatMessage(chainMessages.unnamedApprover),
  );
  const blocked = blockedReasonOf(approver, state);

  if (!blocked) {
    const { Icon: StateIcon, label } = APPROVER_PRESENTATION[state];
    return (
      <div className={styles.approverLine}>
        <Glyph
          icon={StateIcon}
          label={intl.formatMessage(label)}
          mark={APPROVER_MARK[state]}
        />
        <p className={styles.approverName}>{name}</p>
      </div>
    );
  }

  // One who left the account is replaced in place, so for a reader who may
  // manage approval rules the line carries the control rather than a next step.
  // Anyone else gets no control, not a disabled one: the next step on the name
  // already says who can replace them.
  if (blocked === 'removed' && onReplace) {
    return (
      <div>
        <div className={styles.approverLine}>
          <Glyph
            icon={ExclamationCircleOutlineIcon}
            label={intl.formatMessage(chainMessages.approverCannotApprove)}
            mark="blocked"
          />
          <p className={styles.approverName}>{name}</p>
        </div>
        {/* The reason and the link wrap as two pieces. The clip hides the
            separator whenever the link starts a line of its own, so a narrow
            column never leaves it hanging after the reason. */}
        <div className={styles.reasonClip}>
          <p className={classNames(styles.blockedReason, styles.reasonPieces)}>
            <span className={styles.reasonLead}>
              <FormattedMessage {...messages.approverLeftAccount} />
            </span>
            <span>
              <span aria-hidden className={styles.reasonSeparator}>
                ·
              </span>
              <ReplaceControl onClick={onReplace} />
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.approverLine}>
        <Glyph
          icon={ExclamationCircleOutlineIcon}
          label={intl.formatMessage(chainMessages.approverCannotApprove)}
          mark="blocked"
        />
        <NextStep
          step={
            blocked === 'removed'
              ? messages.replaceApproverAskManager
              : messages.approverLostPermissionNextStep
          }
        >
          <span className={styles.approverName}>{name}</span>
        </NextStep>
      </div>
      <p className={styles.blockedReason}>
        <FormattedMessage
          {...(blocked === 'removed'
            ? messages.approverLeftAccount
            : messages.approverLostPermission)}
        />
      </p>
    </div>
  );
};

const NoApproverLine: React.FC = () => {
  const styles = useStyles();

  return (
    <div className={styles.approverLine}>
      <Glyph icon={ExclamationCircleOutlineIcon} mark="blocked" />
      <NextStep step={messages.noApproverNextStep}>
        <span className={styles.noApprover}>
          <FormattedMessage {...messages.noApprover} />
        </span>
      </NextStep>
    </div>
  );
};

/**
 * The approvers one level of a round shows, each read against its own level's
 * state rather than the round's.
 *
 * In a round the request waits on, a level that is not itself waiting waits on
 * nobody: another rule's level may already be approved, or not reached yet.
 * Its unanswered approvers keep a pending assignment, and a departed one keeps
 * its `blocked_reason`, so listing them would name people nobody waits on. Only
 * the ones who answered stay.
 */
const memberApprovers = (member: RoundMember, roundState: LevelState) =>
  // `visibleApprovers` filters the level's own rows, which are the queue's.
  (visibleApprovers(member.level) as Approver[])
    .filter(
      approver =>
        roundState !== 'active' ||
        member.state === 'active' ||
        approver.status !== 'pending',
    )
    .map(approver => ({
      approver,
      state: approverState(approver, member.state),
    }));

// Only a level the request waits on can lack the approver it waits for.
const waitsOnNobody = (member: RoundMember) =>
  member.state === 'active' && member.level.needsAttention;

/** Who the waiting level is waiting on, under its line. */
const RoundApprovers: React.FC<{
  round: ApprovalRound;
  canManageRules: boolean;
  onReplace: (level: Level, approver: Approver) => void;
}> = ({ round, canManageRules, onReplace }) => {
  const styles = useStyles();

  return (
    <div className={styles.roundApprovers}>
      {round.members.map((member, memberIndex) => (
        // Index in the key as well as the step id: a level the server carried
        // from an earlier approval has no step on this request, so every such
        // level shares step id 0.
        <React.Fragment key={`${member.level.stepId}-${memberIndex}`}>
          {waitsOnNobody(member) && <NoApproverLine />}
          {memberApprovers(member, round.state).map(
            ({ approver, state }, index) => (
              <ApproverLine
                approver={approver}
                key={`${approver.id ?? 'unknown'}-${index}`}
                state={state}
                onReplace={
                  canManageRules
                    ? () => onReplace(member.level, approver)
                    : undefined
                }
              />
            ),
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * "N of M approved", only where it is honest: a live `all` level that needs two
 * or more sign-offs, escalated or not, as the review panel prints it. A round
 * that holds levels from several rules is no one level to count toward, so it
 * prints none.
 */
const roundProgress = (round: ApprovalRound, requestSettled: boolean) => {
  if (round.members.length !== 1) return null;
  return levelProgress(round.members[0].level, requestSettled);
};

/** The card a level line opens: its state, then each approver's. */
const LevelCard: React.FC<{
  round: ApprovalRound;
  progress: { approved: number; total: number } | null;
}> = ({ round, progress }) => {
  const styles = useStyles();
  const intl = useIntl();
  const { Icon: StateIcon, label } = LEVEL_PRESENTATION[round.state];
  const rows = round.members.flatMap(member =>
    memberApprovers(member, round.state),
  );
  const unnamed = intl.formatMessage(chainMessages.unnamedApprover);
  const noApprover = round.members.some(waitsOnNobody);

  return (
    <>
      <div className={styles.cardHeader}>
        <Glyph icon={StateIcon} mark={LEVEL_MARK[round.state]} />
        <span className={styles.cardState}>
          <FormattedMessage {...label} />
        </span>
      </div>
      {progress && (
        <p className={styles.cardProgress}>
          <FormattedMessage
            {...chainMessages.levelProgress}
            values={progress}
          />
        </p>
      )}
      {noApprover && (
        <p className={styles.cardNote}>
          <FormattedMessage {...messages.noApproverCardNote} />
        </p>
      )}
      {rows.length > 0 && (
        <div className={styles.cardApprovers}>
          {rows.map(({ approver, state }, index) => {
            const blocked = blockedReasonOf(approver, state);
            const presentation = APPROVER_PRESENTATION[state];
            return (
              <div
                className={styles.cardApprover}
                key={`${approver.id ?? 'unknown'}-${index}`}
              >
                <div className={styles.cardApproverLine}>
                  <Glyph
                    icon={
                      blocked ? ExclamationCircleOutlineIcon : presentation.Icon
                    }
                    mark={blocked ? 'blocked' : APPROVER_MARK[state]}
                  />
                  <span className={styles.cardApproverName}>
                    {approverName(approver, unnamed)}
                  </span>
                  {/* An escalated level lists its backups beside the primaries
                      the count is made of, so the count needs them told apart. */}
                  {approver.backup && (
                    <span className={styles.cardBackup}>
                      <FormattedMessage {...chainMessages.backupApprover} />
                    </span>
                  )}
                  {/* The chain's word for this case, in place of Pending: a
                      blocked approver must never read as merely pending. */}
                  <span className={styles.cardApproverState}>
                    <FormattedMessage
                      {...(blocked
                        ? chainMessages.approverCannotApprove
                        : presentation.label)}
                    />
                  </span>
                </div>
                {blocked && (
                  <p className={styles.cardBlockedReason}>
                    <FormattedMessage
                      {...(blocked === 'removed'
                        ? messages.approverLeftAccount
                        : messages.approverLostPermission)}
                    />
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const RoundLine: React.FC<{
  round: ApprovalRound;
  requestSettled: boolean;
}> = ({ round, requestSettled }) => {
  const styles = useStyles();
  const intl = useIntl();
  const progressId = useId();
  const { Icon: StateIcon, label } = LEVEL_PRESENTATION[round.state];
  const progress = roundProgress(round, requestSettled);
  const logic = round.approverLogic
    ? intl.formatMessage(
        round.approverLogic === 'all'
          ? ruleMessages.approverLogicAll
          : ruleMessages.approverLogicAny,
      )
    : null;

  let text: string;
  if (logic) {
    text = round.name
      ? intl.formatMessage(messages.roundLabelNamed, {
          number: round.position,
          name: round.name,
          logic,
        })
      : intl.formatMessage(messages.roundLabel, {
          number: round.position,
          logic,
        });
  } else {
    // The round's levels disagree on Any/All, so neither word names it.
    text = round.name
      ? intl.formatMessage(ruleMessages.levelRowNamed, {
          number: round.position,
          name: round.name,
        })
      : intl.formatMessage(ruleMessages.levelRow, { number: round.position });
  }

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <div
            aria-describedby={progress ? progressId : undefined}
            className={styles.roundLine}
            tabIndex={0}
          />
        }
      >
        <Glyph
          icon={StateIcon}
          label={intl.formatMessage(label)}
          mark={LEVEL_MARK[round.state]}
        />
        <span className={styles.roundLabel}>{text}</span>
      </Tooltip.Trigger>
      {progress && (
        <span hidden id={progressId}>
          {intl.formatMessage(chainMessages.levelProgress, progress)}
        </span>
      )}
      <Tooltip.Content
        align="start"
        className={styles.levelCard}
        color="light"
        showArrow={false}
        side="bottom"
        sideOffset={4}
      >
        <LevelCard progress={progress} round={round} />
      </Tooltip.Content>
    </Tooltip>
  );
};

/**
 * How far one approval has got, and who it waits on — the queue's Approval
 * status column.
 *
 * One line per round: the levels asked at one moment, which several rules can
 * contribute to (`approvalRounds`). The line is the round's label alone; its
 * glyph carries the state, and its card says it in words on hover and on
 * focus. Under the round a request waits on, each approver is named with their
 * own glyph, and one who cannot act says why. One who left the account carries
 * Replace approver for a reader who may manage approval rules; otherwise the
 * next step is on hover and on focus.
 *
 * State comes from `levelState`, and words and glyphs from
 * `LEVEL_PRESENTATION` and `APPROVER_PRESENTATION`, all shared with the review
 * panel's rail.
 */
export const ApprovalStatusCell: React.FC<ApprovalStatusCellProps> = ({
  rules,
  requestSettled,
  canManageRules,
  onReplace,
}) => {
  const styles = useStyles();
  const rounds = approvalRounds(rules, requestSettled);

  if (rounds.length === 0) {
    return (
      <Typography color="textSecondary" variant="body2">
        <FormattedMessage {...messages.noLevels} />
      </Typography>
    );
  }

  return (
    <div className={styles.statusCell} data-testid="approval-status">
      {rounds.map(round => (
        <React.Fragment key={round.position}>
          <RoundLine requestSettled={requestSettled} round={round} />
          {round.state === 'active' && (
            <RoundApprovers
              canManageRules={canManageRules}
              round={round}
              onReplace={onReplace}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
